import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Waves } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";

const NAV = ["home", "about", "lessons", "prices", "gallery", "events", "location", "contact"];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, setLang } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const bookNow = () => {
    setOpen(false);
    navigate("/book");
  };

  const onDark = !scrolled && location.pathname === "/";

  const LangBtns = ({ mobile }) => (
    <div className={`flex items-center rounded-full border text-[0.68rem] font-mono overflow-hidden ${mobile ? "border-white/30" : onDark ? "border-sand-100/40" : "border-ocean/20"}`}>
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 uppercase transition-colors ${lang === l ? "bg-coral text-white" : mobile ? "text-sand-100/80" : onDark ? "text-sand-100/80" : "text-ocean/70"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-sand-100/85 backdrop-blur-xl border-b border-ocean/10 py-2"
          : "bg-transparent py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
        <button onClick={() => goTo("home")} data-testid="nav-logo-button" className="flex items-center shrink-0">
          <Logo variant={onDark ? "light" : "dark"} className="h-10 sm:h-12 w-auto transition-all" />
        </button>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {NAV.map((id) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => goTo(id)}
              className={`font-mono text-[0.68rem] tracking-[0.18em] transition-colors hover:text-gold ${
                onDark ? "text-sand-100/90" : "text-ocean/80"
              }`}
            >
              {t(`nav.${id}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LangBtns />
          <button
            onClick={bookNow}
            data-testid="nav-book-now-button"
            className="hidden sm:inline-flex items-center gap-2 bg-coral hover:bg-coral/90 text-white font-heading font-bold text-sm tracking-wide px-5 py-2.5 rounded-full transition-all hover:scale-[1.03] shadow-lg shadow-coral/20"
          >
            <Waves className="w-4 h-4" /> {t("cta.bookNow")}
          </button>
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            className={`lg:hidden p-2 rounded-md ${onDark ? "text-sand-100" : "text-ocean"}`}
            aria-label="Apri menu"
          >
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-ocean-900/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
        <div
          className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-ocean-900 text-sand-100 p-6 flex flex-col transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
          data-testid="mobile-menu-drawer"
        >
          <div className="flex items-center justify-between mb-8">
            <Logo variant="light" className="h-10 w-auto" />
            <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Chiudi menu">
              <X className="w-7 h-7" />
            </button>
          </div>
          <nav className="flex flex-col gap-1">
            {NAV.map((id) => (
              <button
                key={id}
                data-testid={`mobile-nav-link-${id}`}
                onClick={() => goTo(id)}
                className="text-left font-heading text-2xl font-bold py-3 border-b border-white/10 hover:text-gold transition-colors"
              >
                {t(`nav.${id}`)}
              </button>
            ))}
          </nav>
          <div className="mt-6"><LangBtns mobile /></div>
          <button
            onClick={bookNow}
            data-testid="mobile-book-now-button"
            className="mt-auto inline-flex items-center justify-center gap-2 bg-coral text-white font-heading font-bold text-lg px-6 py-4 rounded-full"
          >
            <Waves className="w-5 h-5" /> {t("cta.bookNow")}
          </button>
        </div>
      </div>
    </header>
  );
};
